import React, {
    InputHTMLAttributes,
    useCallback,
    useMemo,
    useState,
} from 'react';

import { RequiredAsterisk } from 'components/ui/RequiredAsterisk';

import { colors, Fonts, spacing } from 'styles/theme';
import { Bumper, Label, SubLabel } from 'styles/shared';
import {
    TextInput as StyledTextInput,
    TextArea as StyledTextArea,
    TextInputProps as StyledTextInputProps,
    TextInputWrapper,
} from 'styles/textInput';

type HTMLInputCustomProps = InputHTMLAttributes<
    HTMLInputElement | HTMLTextAreaElement
> &
    StyledTextInputProps;
interface TextInputProps extends HTMLInputCustomProps {
    label?: string;
    subLabel?: string;
    isTextArea?: boolean;
    $isError?: boolean;
    $width?: string;
    $isAltText?: boolean;
}

export const TextInput = (props: TextInputProps) => {
    const {
        label,
        subLabel,
        isTextArea,
        $isError,
        required,
        $width,
        $isAltText,
    } = props;

    const [isFocused, setIsFocused] = useState<boolean>(false);

    const onFocus = useCallback(() => {
        if (!isFocused) {
            setIsFocused(true);
        }
    }, [isFocused]);

    const onBlur = useCallback(() => {
        if (isFocused) {
            setIsFocused(false);
        }
    }, [isFocused]);

    const borderColor = useMemo(() => {
        if ($isError) {
            return colors.error;
        } else if (isFocused) {
            return colors.focus;
        }
    }, [$isError, isFocused]);

    const renderedTextInputArea = useMemo(
        () =>
            isTextArea ? (
                <StyledTextArea
                    {...props}
                    rows={3}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    $borderColor={borderColor}
                />
            ) : (
                <StyledTextInput
                    {...props}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    $borderColor={borderColor}
                />
            ),
        [borderColor, isTextArea, onBlur, onFocus, props]
    );

    return (
        <TextInputWrapper $width={$width}>
            {$isAltText ? (
                <>
                    {label == null ? null : (
                        <Fonts.Medium $isOpenSansSemiBold>{label}</Fonts.Medium>
                    )}
                    {subLabel == null ? null : (
                        <Fonts.Medium>
                            {subLabel}
                            {required ? <RequiredAsterisk /> : null}
                        </Fonts.Medium>
                    )}
                    <Bumper $minHeight={spacing.xxSmall} />
                </>
            ) : (
                <>
                    {label == null ? null : (
                        <Label>
                            {label}
                            {required ? <RequiredAsterisk /> : null}
                        </Label>
                    )}
                    {subLabel == null ? null : <SubLabel>{subLabel}</SubLabel>}
                </>
            )}
            {renderedTextInputArea}
        </TextInputWrapper>
    );
};
